/**
 * The ownership arms a rep's book is built from, as shared data.
 *
 * The SQL for each arm lives server-side in `services/owned-accounts/read-from-mirror.ts`;
 * this module owns only the IDS, the rep-facing LABELS, and which arms each role has, so
 * the browser and the server name the same routes. `ARMS_BY_USER_TYPE` there is typed
 * against these ids, and `__tests__/read-from-mirror.test.ts` pins the two together.
 */
import { z } from 'zod';

export const OWNERSHIP_ARM_IDS = [
  'account-owner',
  'growth-strategist',
  'account-claydr',
  'open-opp-owner',
  'open-opp-claydr',
] as const;

export const OwnershipArmIdSchema = z.enum(OWNERSHIP_ARM_IDS);
export type OwnershipArmId = z.infer<typeof OwnershipArmIdSchema>;

/** Rep-facing wording, first person: the rep reads these as "why is this account mine". */
export const OWNERSHIP_ARM_LABEL: Record<OwnershipArmId, string> = {
  'account-owner': 'Account owner is me',
  'growth-strategist': 'Growth Strategist is me',
  'account-claydr': 'ClayDR is me',
  'open-opp-owner': 'I own an open opportunity',
  'open-opp-claydr': "I'm the ClayDR on an open opportunity",
};

/** Keyed by the owned-accounts registry's user types; arm order matches the server's. */
export const OWNERSHIP_ARMS_BY_USER_TYPE: Record<'PRE_SALES' | 'POST_SALES' | 'SDR', readonly OwnershipArmId[]> = {
  PRE_SALES: ['account-owner', 'open-opp-owner'],
  POST_SALES: ['growth-strategist', 'open-opp-owner'],
  SDR: ['account-claydr', 'open-opp-owner', 'open-opp-claydr'],
};

/** The routes `userType` has, in the role's order. A role outside the registry has none. */
export function ownershipArmsFor(userType: string | null | undefined): readonly OwnershipArmId[] {
  return userType && Object.prototype.hasOwnProperty.call(OWNERSHIP_ARMS_BY_USER_TYPE, userType)
    ? OWNERSHIP_ARMS_BY_USER_TYPE[userType as keyof typeof OWNERSHIP_ARMS_BY_USER_TYPE]
    : [];
}
